import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Flex, RadioGroup, Text as RadixText } from '@radix-ui/themes';

import { useBroadcastContext } from '../contexts/BroadcastContext.jsx';
import { useSmsPreview } from '../hooks/useSmsPreview';
import { useUpdateRecipientFieldMutation } from '../hooks/useRecipientFieldMutations';
import {
  missingCount,
  referencedRecipientFields,
} from '../utils/recipientFields';
import { ME, substituteRecipientTokens } from '../utils/recipientPreview';
import {
  PRIMARY_AIRBO_LINK_TOKEN,
  normalizeExtraAirboLinks,
} from '../utils/airboLinkTokens';
import { EMAIL, SMS, BOTH } from '../utils/constants';

import { PreviewAsSelect } from './PreviewAsSelect.jsx';
import { Summary } from './Summary.jsx';

const decodeHtmlEntities = text => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

// Replaces Click Here placeholder with proper case based on sentence position
const replaceWithProperCase = (text, placeholder, replacement) => {
  const regex = new RegExp(`(${placeholder})`, 'g');

  return text.replace(regex, (_, __, offset) => {
    const cleanTextBefore = decodeHtmlEntities(
      text.substring(0, offset).replace(/<[^>]*>/g, ''),
    ).trim();

    // Check for emoji at the end of the text
    const emojiRegex = /\p{Emoji}/u;
    const endsWithEmoji =
      cleanTextBefore.length > 0 && emojiRegex.test(cleanTextBefore.slice(-2));

    const shouldCapitalize =
      offset === 0 ||
      cleanTextBefore === '' ||
      ['.', '!', '?'].includes(cleanTextBefore.slice(-1)) ||
      endsWithEmoji;

    return shouldCapitalize
      ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
      : replacement.toLowerCase();
  });
};

export const Review = () => {
  const { t } = useTranslation('broadcasts');
  const history = useHistory();
  const {
    values,
    sendTest,
    userCount,
    emailCount,
    smsCount,
    recipientFields: allFields = [],
    recipientRows = [],
    currentUser = {},
    previewAsUuid,
    setPreviewAsUuid,
    previewPerson,
  } = useBroadcastContext();
  const { previewMessage } = useSmsPreview(values.smsBody);
  const { updateRecipientFieldMutation } = useUpdateRecipientFieldMutation();

  // Only consider recipient fields referenced in the bodies that will
  // actually send: a field referenced only in a channel this broadcast isn't
  // sending (e.g. leftover sms tokens on an email-only broadcast) shouldn't
  // drive the missing-value banner or the preview substitution.
  const sendingEmailBody = [EMAIL, BOTH].includes(values.broadcastType)
    ? values.emailBody
    : null;
  const sendingSmsBody = [SMS, BOTH].includes(values.broadcastType)
    ? values.smsBody
    : null;
  const recipientFields = referencedRecipientFields(
    allFields,
    sendingEmailBody,
    sendingSmsBody,
  );
  const missingFields = recipientFields.filter(
    field => missingCount(field.filledCount, userCount) > 0,
  );

  const handleSkipMissingChange = (field, radioValue) => {
    // Name/key are re-sent unchanged so BroadcastRecipientFields::Upsert's
    // presence validations don't fail on this partial update; rows is
    // intentionally omitted since this toggle should not touch saved values.
    updateRecipientFieldMutation({
      broadcastMessageUuid: values.uuid,
      id: field.id,
      name: field.name,
      key: field.key,
      skipMissing: radioValue === 'skip',
    });
  };

  const previewValues = {
    ...values,
    // The text as written, so a text-only Broadcast's image is never named
    // after the person it's previewed as.
    smsTemplate: values.smsBody,
    smsBody: substituteRecipientTokens(previewMessage, {
      person: previewPerson,
      recipientFields: allFields,
      t,
    }),
    emailBody: substituteRecipientTokens(
      replaceWithProperCase(
        normalizeExtraAirboLinks(values.emailBody),
        PRIMARY_AIRBO_LINK_TOKEN,
        'click here',
      ),
      { person: previewPerson, recipientFields: allFields, t },
    ),
  };

  const isMeSelected = !previewAsUuid || previewAsUuid === ME;
  const sendTestLabel = isMeSelected
    ? t('reviewStep.sendTestBtn')
    : t('reviewStep.sendTestAsBtn', { name: previewPerson?.firstName });

  const handleSendTest = () => {
    sendTest(isMeSelected ? undefined : previewAsUuid, previewPerson?.name);
  };

  return (
    <>
      {missingFields.length > 0 && (
        <BannerList>
          {missingFields.map(field => {
            const missing = missingCount(field.filledCount, userCount);
            const missingNames = recipientRows
              .filter(row => !field.values?.[row.userUuid])
              .map(row => row.name);
            const shownNames = missingNames.slice(0, 3);
            const moreCount = missingNames.length - shownNames.length;

            return (
              <Banner key={field.id}>
                <RadixText size="2" weight="medium">
                  {moreCount > 0
                    ? t('reviewStep.recipientFieldMissing.titleWithMore', {
                        count: missing,
                        total: userCount,
                        name: field.name,
                        names: shownNames.join(', '),
                        more: moreCount,
                      })
                    : t('reviewStep.recipientFieldMissing.title', {
                        count: missing,
                        total: userCount,
                        name: field.name,
                        names: shownNames.join(', '),
                      })}{' '}
                  <FillItLink
                    onClick={() => history.push(`/edit/email/${values.uuid}`)}
                  >
                    {t('reviewStep.recipientFieldMissing.fillItIn')}
                  </FillItLink>
                </RadixText>
                <RadioGroup.Root
                  value={field.skipMissing ? 'skip' : 'blank'}
                  onValueChange={radioValue =>
                    handleSkipMissingChange(field, radioValue)
                  }
                >
                  <Flex gap="4" mt="2">
                    <RadixText as="label" size="2">
                      <Flex gap="1" align="center">
                        <RadioGroup.Item value="skip" />
                        {t('reviewStep.recipientFieldMissing.skip')}
                      </Flex>
                    </RadixText>
                    <RadixText as="label" size="2">
                      <Flex gap="1" align="center">
                        <RadioGroup.Item value="blank" />
                        {t('reviewStep.recipientFieldMissing.sendBlank')}
                      </Flex>
                    </RadixText>
                  </Flex>
                </RadioGroup.Root>
              </Banner>
            );
          })}
        </BannerList>
      )}
      <Summary
        header={t('reviewStep.header')}
        values={previewValues}
        sendTest={handleSendTest}
        sendTestLabel={sendTestLabel}
        userCount={userCount}
        emailCount={emailCount}
        smsCount={smsCount}
        audiences={values.audiences}
        rightContent={
          recipientRows.length > 0 && (
            <PreviewAsControl>
              <RadixText size="1" weight="medium" color="gray" wrap="nowrap">
                {t('reviewStep.previewAs')}
              </RadixText>
              <PreviewAsSelect
                recipientRows={recipientRows}
                value={previewAsUuid}
                selectedName={previewPerson?.name}
                meName={currentUser.name}
                onChange={setPreviewAsUuid}
              />
            </PreviewAsControl>
          )
        }
      />
    </>
  );
};

const BannerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 20px 0;
`;

const Banner = styled.div`
  background: #fff8e6;
  border: 1px solid #f5c04d;
  border-radius: 8px;
  padding: 12px 16px;
`;

const FillItLink = styled.button`
  all: unset;
  cursor: pointer;
  color: var(--accent-11);
  font-weight: 600;
`;

const PreviewAsControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
